import { Component, Injector, OnInit } from '@angular/core';
import { BaseComponent } from '../../../base/base.component';
import { AthleteService } from '../../../../services/athlete/athlete.service';
import { CategoryService } from '../../../../services/category/category.service';
import { ConvocationService } from '../../../../services/convocation/convocation.service';
import { SeasonService } from '../../../../services/season/season.service';
import { ConvocationRowItem } from '../../dashboard-mister/mister-convocation-shared';

type ConvocationTab = 'upcoming' | 'past';

interface ConvocationMonthGroup {
    key: string;
    date: string;
    items: ConvocationRowItem[];
}

const PAST_PAGE_SIZE = 20;

@Component({
    selector: 'app-athlete-convocations',
    templateUrl: './athlete-convocations.component.html',
    styleUrls: ['./athlete-convocations.component.scss'],
})
export class AthleteConvocationsComponent extends BaseComponent implements OnInit {
    activeTab: ConvocationTab = 'upcoming';

    upcoming: ConvocationRowItem[] = [];
    upcomingLoading = true;

    past: ConvocationRowItem[] = [];
    pastTotal = 0;
    pastLoading = true;
    pastLoadingMore = false;

    readonly detailRouteBase = ['/users/convocations'];

    private categoryLabelById = new Map<string, string>();
    private currentAthleteId?: string;
    private readonly companyId = localStorage.getItem('wisper_companyId') ?? '';

    constructor(
        public serviceInjector: Injector,
        private convocationService: ConvocationService,
        private categoryService: CategoryService,
        private seasonService: SeasonService,
        private athleteService: AthleteService,
    ) {
        super(serviceInjector);
    }

    async ngOnInit(): Promise<void> {
        await Promise.all([this.loadCategoryLabels(), this.loadCurrentAthleteId()]);
        await Promise.all([this.loadUpcoming(), this.loadPast()]);
    }

    get nextConvocation(): ConvocationRowItem | undefined {
        return this.upcoming[0];
    }

    get laterUpcomingGroups(): ConvocationMonthGroup[] {
        return this.groupByMonth(this.upcoming.slice(1));
    }

    get pastGroups(): ConvocationMonthGroup[] {
        return this.groupByMonth(this.past);
    }

    get hasMorePast(): boolean {
        return this.past.length < this.pastTotal;
    }

    selectTab(tab: ConvocationTab): void {
        this.activeTab = tab;
    }

    /** `true`/`false` quando sappiamo chi è l'atleta, `null` se non determinabile. */
    isInvited(c: ConvocationRowItem): boolean | null {
        if (!this.currentAthleteId || !c.users) {
            return null;
        }
        return c.users.includes(this.currentAthleteId);
    }

    isToday(c: ConvocationRowItem): boolean {
        return new Date(c.date).toDateString() === new Date().toDateString();
    }

    daysUntil(c: ConvocationRowItem): number {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const target = new Date(c.date);
        target.setHours(0, 0, 0, 0);
        return Math.round((target.getTime() - start.getTime()) / 86_400_000);
    }

    isLink(value: string): boolean {
        return /^https?:\/\//i.test(value?.trim() ?? '');
    }

    fullAddress(c: ConvocationRowItem): string {
        const city = [c.city, c.province && c.province !== '—' ? `(${c.province})` : '']
            .filter(Boolean)
            .join(' ');
        return [c.address, city].filter((part) => part && part.trim()).join(', ');
    }

    trackById(_index: number, item: ConvocationRowItem): string {
        return item.id;
    }

    trackByKey(_index: number, group: ConvocationMonthGroup): string {
        return group.key;
    }

    async loadMorePast(): Promise<void> {
        if (this.pastLoadingMore || !this.hasMorePast) {
            return;
        }
        this.pastLoadingMore = true;
        try {
            const { rows, count } = await this.fetchPage('past', this.past.length);
            this.past = [...this.past, ...rows];
            this.pastTotal = count;
        } finally {
            this.pastLoadingMore = false;
        }
    }

    navigateBackToDashboard(): void {
        void this.navigate.navigate(['/users/dashboard']);
    }

    private async loadUpcoming(): Promise<void> {
        this.upcomingLoading = true;
        try {
            const { rows } = await this.fetchPage('upcoming', 0, 100);
            this.upcoming = rows;
        } finally {
            this.upcomingLoading = false;
        }
    }

    private async loadPast(): Promise<void> {
        this.pastLoading = true;
        try {
            const { rows, count } = await this.fetchPage('past', 0);
            this.past = rows;
            this.pastTotal = count;
        } finally {
            this.pastLoading = false;
        }
    }

    private async fetchPage(
        period: ConvocationTab,
        skip: number,
        top = PAST_PAGE_SIZE,
    ): Promise<{ rows: ConvocationRowItem[]; count: number }> {
        const res = await this.convocationService.listConvocations({
            period,
            $top: top,
            $skip: skip,
        });
        const rawRows = this.convocationService.extractRows(res);
        const rows = rawRows
            .filter((r: any) => (r?.status ?? '').toLowerCase() !== 'deleted')
            .map((r: any) =>
                this.convocationService.mapApiRecordToRow(
                    r,
                    r?.categoryId ? this.categoryLabelById.get(r.categoryId) : undefined,
                ),
            );
        const count = Number(res?.data?.count ?? rawRows.length);
        return { rows, count };
    }

    private groupByMonth(items: ConvocationRowItem[]): ConvocationMonthGroup[] {
        const groups: ConvocationMonthGroup[] = [];
        for (const item of items) {
            const d = new Date(item.date);
            const key = `${d.getFullYear()}-${d.getMonth()}`;
            let group = groups[groups.length - 1];
            if (!group || group.key !== key) {
                group = { key, date: item.date, items: [] };
                groups.push(group);
            }
            group.items.push(item);
        }
        return groups;
    }

    private async loadCategoryLabels(): Promise<void> {
        this.categoryLabelById.clear();
        if (!this.companyId) {
            return;
        }
        try {
            const seasonsRes = await this.seasonService.getSeasons(
                this.companyId,
                { isCurrent: true },
            );
            const seasonId = seasonsRes?.data?.[0]?.id as string | undefined;
            const all = await this.categoryService.getCategories(
                this.companyId,
                false,
                seasonId ? { seasonId } : undefined,
            );
            for (const c of all) {
                if (!c.id) {
                    continue;
                }
                const label = c.parent
                    ? `${c.parent.name} - ${c.name}`
                    : c.name;
                this.categoryLabelById.set(c.id, label);
            }
        } catch {
            /* ignore */
        }
    }

    private async loadCurrentAthleteId(): Promise<void> {
        const userId = localStorage.getItem('wisper_userId') ?? '';
        if (!userId) {
            return;
        }
        try {
            const response: any = await this.athleteService.getAllAthlete({ userId });
            this.currentAthleteId = response?.data?.[0]?.id as string | undefined;
        } catch {
            this.currentAthleteId = undefined;
        }
    }
}
